import { styled, xStyledCommonProps, toMobile } from '@elliemae/ds-system';
import { DSButtonV2 } from '@elliemae/ds-button-v2';
import { handleAnimation, handleBorderColor } from './utils/styleHelpers.js';
import type { MOBILE_BANNER_TYPES } from './utils/bannerTypes.js';

type MobileBannerTypesT = (typeof MOBILE_BANNER_TYPES)[keyof typeof MOBILE_BANNER_TYPES];

interface StyledBannerContainerT {
  type: MobileBannerTypesT;
  isOpen: boolean;
  isAnimating: boolean;
  height: number;
}

interface StyledInnerContainerT {
  type: MobileBannerTypesT;
  isOpen: boolean;
  isAnimating: boolean;
  ref: React.MutableRefObject<HTMLDivElement>;
}

interface StyledContentT {
  showCloseButton: boolean;
}
export const StyledInnerContainer = styled.div<StyledInnerContainerT>`
  display: flex;
  min-height: 60px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.neutral['000']};
  border-bottom: 4px solid ${({ type, theme }) => handleBorderColor(type, theme)};
  transform: translateY(${({ isOpen }) => (isOpen ? '0' : '-100%')});
  ${({ isAnimating, isOpen }) => handleAnimation(isAnimating, isOpen)};
  padding: 8px 8px 8px 16px;
`;

export const StyledTextContent = styled.div`
  width: 100%;
`;

export const StyledTitle = styled.div`
  color: ${({ theme }) => theme.colors.neutral[700]};
  font-size: ${({ theme }) => toMobile(theme.fontSizes.title[700])};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.space.xxxs};
  @media not all and (min-resolution: 0.001dpcm) {
    @media {
      -webkit-font-smoothing: subpixel-antialiased; // fix safari bold render
      -webkit-text-stroke: 0.4px ${({ theme }) => theme.colors.neutral[700]}; // fix safari bold render
    }
  }
  @media screen and (min--moz-device-pixel-ratio: 0) {
    -webkit-font-smoothing: subpixel-antialiased; // fix safari bold render
    -webkit-text-stroke: 0.4px ${({ theme }) => theme.colors.neutral[700]}; // fix safari bold render
  }
`;

export const StyledSubTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;

  font-size: ${({ theme }) => toMobile(theme.fontSizes.title[600])};
  font-weight: ${({ theme }) => theme.fontWeights.regular};

  & > * {
    margin-top: 4px;
  }
`;

export const StyledIconContainer = styled.div`
  margin-right: ${({ theme }) => theme.space.xxs};
`;

export const StyledContent = styled.div<StyledContentT>`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-right: ${({ showCloseButton }) => (!showCloseButton ? '28px' : '0')};
`;

export const StyledCloseButtonContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const StyledSubTitleText = styled.span`
  margin-right: 16px;
  line-height: 1.1;
`;

export const StyledCloseButton = styled(DSButtonV2)`
  position: relative;
  &:focus {
    &:after {
      display: block;
      content: ' ';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 2px solid ${({ theme }) => theme.colors.brand[700]};
      border-radius: 2px;
      pointer-events: none;
    }
  }
`;

export const StyledActionLink = styled.a`
  text-decoration: none;
  line-height: 1.1;
  font-size: ${({ theme }) => toMobile(theme.fontSizes.title[600])};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.brand[600]};
`;

export const StyledBannerContainer = styled('div')<StyledBannerContainerT>`
  overflow: hidden;
  height: ${({ isOpen }) => (isOpen ? 'auto' : '0px')};
  ${({ isAnimating, isOpen, height }) => handleAnimation(isAnimating, isOpen, height)};
  ${xStyledCommonProps}
`;
